import React, { useState, useMemo } from 'react'

import Validation from 'src/components/Validation'
import {
  PluginErrorBoundary, pluginErrorHandler, pluginData, pluginErrors,
} from 'src/widgets/plugins/pledge'

// The plugin_data payload the backend expects: Order#plugin_data= unwraps
// { __value: [...] } into the stored JSON array (same shape v6 sends). Ordered
// by entry id for stability. Pure + exported so it's unit-testable without
// rendering the hook.
//
// Only plugins that collected data contribute — the gate is `dataById[id]`,
// i.e. the hook called setData (or setDataDefaults) at least once. A plugin
// whose slot the donor never reached, OR whose field rendered but was left
// untouched, has no dataset and submits nothing — so `finalize` does NOT run
// for it (a finalize meant to derive a default like `{ subscribed: false }`
// won't fire unless the hook seeds a default via setDataDefaults). Each
// plugin's optional `finalize({ ...context, values })` derives the submitted
// shape from its collected `values` (v7-only — see the plugin contract) and
// must return a plain object (a hash, matching what Order#plugin_data= /
// the Ruby serializers expect); a non-object, an array, or a throw falls back
// to the raw values so it can't break the order.
export const collectPluginData = (entries, dataById, context = {}, disabledIds = new Set()) => {
  const datasets = entries
    // Skip disabled plugins so a plugin the error boundary killed mid-checkout
    // doesn't re-submit its data or re-run the finalize that crashed it —
    // matching the disabledIds gate in renderSlot / validateSlots. NOTE this is
    // per-request only: if an earlier saveDraft already persisted this plugin's
    // data, the resulting `plugin_data: null` can't clear it — Rails drops a
    // null from `permit(plugin_data: {})`, so the order keeps the earlier
    // dataset. Accepted: the data was collected legitimately before the crash.
    .filter(({ id }) => dataById[id] && !disabledIds.has(id))
    .map(({ id, plugin }) => {
      const values = dataById[id]
      if (!plugin.finalize) return values

      try {
        const final = plugin.finalize({ ...context, values })
        // Must be a plain object — an array passes `typeof == 'object'` but the
        // Ruby serializers guard `is_a?(Hash)` and silently drop it, so treat a
        // non-hash return as invalid and fall back to the raw values.
        return final && typeof final == 'object' && !Array.isArray(final) ? final : values
      } catch (error) {
        console.error(error, plugin)
        if (typeof Honeybadger != 'undefined') Honeybadger.notify(error, { context: { plugin } })
        return values
      }
    })
    .filter(dataset => dataset && Object.keys(dataset).length)

  return datasets.length ? { __value: datasets } : null
}

// v7 plugin registry. Reads plugins registered on window.Pledge.widget (queued
// at parse time via Pledge.widget.addPlugin), holds per-plugin data / errors /
// disabled keyed by the entry's auto-assigned id, and exposes render / validate
// / collect for the widget.
//
// v7 is multi-step, so a plugin's `hooks` and `validators` are parallel
// slot-keyed maps (the v7 contract). A page renders the slots it hosts and
// validates only those, so a plugin's validator runs on the page hosting its
// slot — never stranding the donor on an earlier page. Components come from
// Pledge.components (not props); the hook context is { donation } plus the
// data/errors façade.
//
// paymentMethod is deliberately NOT in the context yet. The only v6 plugin
// that reads it does so in a way that won't port cleanly to v7's multi-step
// flow; rather than expose a prop plugins can't use usefully, we withhold it
// until the right v7 shape is settled. Add it back to the render/validate
// context bags (Contact/CryptoForm/ExpressCheckout) + the App pluginData
// closure when that lands.
const usePlugins = ({ i18n, currency, session } = {}) => {
  const entries = useMemo(() => window.Pledge?.widget?.entries ?? [], [])
  const [ dataById, setDataById ] = useState({})
  const [ errorsById, setErrorsById ] = useState({})
  const [ disabledIds, setDisabledIds ] = useState(() => new Set())

  const updateEntry = (setter, id) => update =>
    setter(prev => ({
      ...prev,
      [id]: typeof update == 'function' ? update(prev[id] || {}) : update,
    }))

  const setEntryData   = id => updateEntry(setDataById, id)
  const setEntryErrors = id => updateEntry(setErrorsById, id)

  const onError = id => pluginErrorHandler({
    session,
    disable: () => setDisabledIds(prev => new Set(prev).add(id)),
  })

  // The render-time context + façade a hook receives (components come from
  // Pledge.components, so they're not passed here).
  const contextFor = (id, context) => {
    const setData = setEntryData(id)
    const setErrors = setEntryErrors(id)
    const clearError = key => setErrors(prev => ({ ...prev, [key]: null }))
    return {
      ...context,
      data: pluginData(dataById[id], { setData, clearError }),
      setData,
      setDataDefaults: defaults => setData(prev => ({ ...defaults, ...prev })),
      errors: pluginErrors(errorsById[id], { setErrors }),
      setErrors,
    }
  }

  // Render every plugin's hook for `slot`, each in its own error boundary so a
  // throw disables just that plugin (donors) / surfaces it (staff).
  const renderSlot = (slot, context) =>
    entries.map(({ id, plugin }) => {
      if (disabledIds.has(id)) return null
      const Hook = plugin.hooks?.[slot]
      if (!Hook) return null

      return (
        <PluginErrorBoundary
          key={id}
          onError={onError(id)}
          fallback={() => session?.staff
            ? <div style={{
                padding: '0.5rem', margin: '0.5rem 0', fontSize: '0.875rem',
                background: '#fef3c7', color: '#78350f', borderRadius: '0.375rem',
              }}>
                Plugin crashed rendering the <code>{slot}</code> slot. See console.
              </div>
            : null}
        >
          <Hook {...contextFor(id, context)} />
        </PluginErrorBoundary>
      )
    }).filter(Boolean)

  // Validate the plugins occupying `slots` (the calling page's slots): run each
  // plugin's validator for those slots, set the plugin's errors, and return
  // whether all passed. `data.validate(fieldSet)` gives plugins the same
  // declarative field validation v6 offered.
  const validateSlots = (slots, context) => {
    let valid = true
    const nextErrors = {}

    entries.forEach(({ id, plugin }) => {
      if (disabledIds.has(id)) return
      const validators = slots.map(slot => plugin.validators?.[slot]).filter(Boolean)
      if (!validators.length) return

      const dataset = dataById[id] || {}
      const data = {
        ...dataset,
        get: (key, fallback = '') => dataset[key] ?? fallback,
        validate: fieldSet => Validation(i18n, currency).validate(dataset, { fieldSet }).errors,
      }

      const merged = {}
      validators.forEach(validate => {
        try {
          const errs = validate({ ...context, data })
          if (errs && typeof errs == 'object') Object.assign(merged, errs)
        } catch (error) {
          onError(id)(error, { plugin })
        }
      })

      nextErrors[id] = merged
      if (Object.keys(merged).length) valid = false
    })

    setErrorsById(prev => ({ ...prev, ...nextErrors }))
    return valid
  }

  return {
    renderSlot,
    validateSlots,
    pluginDataParam: context => collectPluginData(entries, dataById, context, disabledIds),
  }
}

export default usePlugins
